<template>
  <VRow no-gutters class="pa-0" justify="center">
    <VCol>
      <VList
        class="py-0"
        active-class="font-weight-medium"
        bg-color="white"
        :density="isSubcategory && !mobile ? 'compact' : undefined"
      >
        <!-- categoryItem.style is applied here so web content can hide categories with d-none. -->
        <!-- Desktop: each category is its own Tab stop. A Vuetify VList is otherwise a single
             Tab stop with arrow keys between items, so Tab from the first category left the
             menu. A row whose action is its inner button (an expander) leaves the Tab
             stop to that button, so it is not reached twice. Mobile keeps the VList
             default. AB#65421 -->
        <VListItem
          v-for="(categoryItem, idx) in displayedChildren"
          :key="categoryItem.id"
          :ripple="false"
          :tabindex="mobile || isActionItem(categoryItem) ? undefined : 0"
          class="justify-space-between list-item-category"
          :class="[
            isSubcategory && !mobile ? 'px-3 desktop-sub-item' : 'px-6',
            categoryItem.style,
            !isSubcategory && idx !== displayedChildren.length - 1
              ? 'border-bottom'
              : '',
            mobile && !isSubcategory && !showChildren[categoryItem.id]
              ? 'border-bottom'
              : '',
            mobile && !isSubcategory && idx === 0 ? 'border-top' : '',
            mobile && !isSubcategory
              ? 'mobile-top-item font-weight-semibold'
              : '',
            mobile && isSubcategory
              ? 'mobile-sub-item font-weight-regular pl-4 pr-0'
              : '',
          ]"
          :to="getItemTarget(categoryItem)"
          @click="clickAction(categoryItem)"
        >
          <component
            :is="isActionItem(categoryItem) ? 'button' : 'div'"
            class="category-item-trigger"
            :type="isActionItem(categoryItem) ? 'button' : undefined"
            :aria-expanded="
              haveChildren(categoryItem)
                ? Boolean(showChildren[categoryItem.id])
                : undefined
            "
            :aria-controls="
              haveChildren(categoryItem)
                ? getChildrenId(categoryItem)
                : undefined
            "
            @click="handleTriggerClick($event, categoryItem)"
          >
            <VRow tag="span" no-gutters align="center">
              <VCol
                tag="span"
                cols="10"
                :class="showChildren[categoryItem.id] ? 'font-weight-bold' : ''"
              >
                {{ categoryItem.name }}
              </VCol>
              <VCol
                v-if="haveChildren(categoryItem)"
                tag="span"
                class="text-right"
                @click.stop.prevent.capture="toggleChildren(categoryItem)"
              >
                <VIcon
                  :color="mobile && !isSubcategory ? 'greyedOut' : 'primary'"
                  :size="mobile && !isSubcategory ? 24 : 20"
                >
                  {{
                    mobile && !isSubcategory
                      ? showChildren[categoryItem.id]
                        ? icons.mdiChevronUpCircleOutline
                        : icons.mdiChevronDownCircleOutline
                      : showChildren[categoryItem.id]
                        ? icons.mdiChevronUp
                        : icons.mdiChevronDown
                  }}
                </VIcon>
              </VCol>
            </VRow>
          </component>
          <div
            v-if="showChildren[categoryItem.id]"
            :id="getChildrenId(categoryItem)"
            class="subcategory-list"
            :class="{ 'mobile-sub-list': mobile && !isSubcategory }"
            @click.stop
          >
            <div v-if="!mobile || isSubcategory" class="subcategory-divider" />
            <MenuItemList
              :category="categoryItem"
              :restrict-expansion="
                restrictExpansion ||
                (categoryItem.slug?.includes(SHOP_ROOT) ?? false)
              "
              :mobile="mobile"
              is-subcategory
              class="flex-grow-1"
              @close="emits('close')"
            />
          </div>
        </VListItem>
        <VListItem
          v-if="showShopSparkitLogo"
          :ripple="false"
          class="sparkit-logo-list-item pa-0"
        >
          <LayoutHeaderSparkitLogo
            :categories="category.children ?? []"
            variant="menu"
            test-id="sparkit-logo-shop"
            @close="emits('close')"
          />
        </VListItem>
      </VList>
    </VCol>
  </VRow>
</template>

<script setup lang="ts">
import {
  mdiChevronDown,
  mdiChevronDownCircleOutline,
  mdiChevronUp,
  mdiChevronUpCircleOutline,
} from '@mdi/js'
import { UserRole } from '~/types/account'
import type { CategoryItem } from '~/types/root'
import { isSparkitCategorySlug } from '~/utils/categoryRoutes'
import { hasVisibleChildren, isDbwsLoginRoute, resolveCategoryTarget } from '~/utils/categoryTarget'
import { PORTAL_DBWS_SUBSCRIBE } from '~/utils/portalRoutes'

interface Props {
  category: CategoryItem
  isSubcategory?: boolean
  restrictExpansion?: boolean
  mobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isSubcategory: false,
  restrictExpansion: false,
  mobile: false,
})

const emits = defineEmits<{
  (e: 'close'): void
}>()

const icons = {
  mdiChevronDown,
  mdiChevronDownCircleOutline,
  mdiChevronUp,
  mdiChevronUpCircleOutline,
}
const { userRole, isDemo } = useSAuthState()
const sparkitVisibleRoles = new Set([
  UserRole.Customer,
  UserRole.Guest,
  UserRole.Anonymous,
  UserRole.Agent,
])
const containsSparkit = (categories: CategoryItem[]): boolean =>
  categories.some((category) =>
    isSparkitCategorySlug(category.slug) || containsSparkit(category.children ?? []),
  )
// Sitefinity authors slugs with its `~` site-root marker — the SparkIt
// department arrives that way (AB#62408) and the Shop node it now sits under
// may too. A strict comparison would drop the dropdown logo with no other
// visible symptom, so normalise before comparing. AB#62633
const isShopRoot = computed(() => (props.category.slug ?? '').replace(/^~/, '') === SHOP_ROOT)
const showShopSparkitLogo = computed(() =>
  !props.mobile &&
  isShopRoot.value &&
  !isDemo.value &&
  sparkitVisibleRoles.has(userRole.value) &&
  containsSparkit(props.category.children ?? []),
)
const { buildCategoryUrl } = useCategoryUrl()
// Resolved in setup: useRouter() is not callable after an await, where the
// Nuxt context is gone.
const router = useRouter()
const showChildren = ref<Record<string, boolean>>({})
// SparkIt renders as the logo on desktop, where the text row would duplicate
// it, and as a plain text row inside the mobile Shop accordion only. The
// drawer carries the logo at the bottom of the menu, so a row anywhere else in
// it would be the duplicate entry design ruled out. AB#62633
const displayedChildren = computed(() =>
  props.category.children?.filter((child) =>
    (props.mobile && isShopRoot.value) || !isSparkitCategorySlug(child.slug),
  ) ?? [],
)

const haveChildren = (categoryItem: CategoryItem): boolean => {
  // SparkIt is a link to its landing page wherever it appears, never an
  // expander — an expander drops the `to` binding, so its own Sitefinity
  // subcategories would leave the entry with nowhere to go. AB#62633
  if (!hasVisibleChildren(categoryItem)) {
    return false
  }

  // Mobile top-level items are accordion sections whenever they contain visible children.
  if (props.mobile && !props.isSubcategory) {
    return true
  }

  // Shop category expansion is explicitly enabled by iconMode. Other trees expand normally.
  if (props.restrictExpansion || categoryItem.slug?.includes(SHOP_ROOT)) {
    return !!categoryItem.iconMode
  }

  return true
}

const resolveTarget = (categoryItem: CategoryItem) =>
  resolveCategoryTarget(categoryItem, haveChildren(categoryItem), buildCategoryUrl)

const getItemTarget = (categoryItem: CategoryItem): string | undefined => {
  const target = resolveTarget(categoryItem)
  return target.kind === 'internal' ? target.to : undefined
}

const isActionItem = (categoryItem: CategoryItem): boolean =>
  getItemTarget(categoryItem) === undefined
const getChildrenId = (categoryItem: CategoryItem): string =>
  `category-children-${categoryItem.id}`

const toggleChildren = (categoryItem: CategoryItem) => {
  if (showChildren.value[categoryItem.id]) {
    showChildren.value[categoryItem.id] = false
  } else if (props.mobile) {
    // Mobile is an accordion: opening one item closes its siblings at this tree level.
    showChildren.value = { [categoryItem.id]: true }
  } else {
    showChildren.value[categoryItem.id] = true
  }
}

const getLinkAction = (categoryItem: CategoryItem) => {
  const target = resolveTarget(categoryItem)
  if (target.kind === 'action') {
    return target.action
  }

  if (target.kind === 'external') {
    return () => {
      openNewWindow(target.url)
      emits('close')
    }
  }

  return undefined
}

const handleDbwsRedirect = async () => {
  // Open the window synchronously so Safari does not block the popup; the
  // redirect URL is filled in once the API responds.
  const newWindow = window.open('about:blank', '_blank')
  try {
    const { redirectUrl } = await $api()<{ redirectUrl: string }>('/account/demo/dbws/redirect', { method: 'post' })
    if (redirectUrl && newWindow) {
      newWindow.location.href = redirectUrl
    } else {
      // No DBWS for this demonstrator — send them to the subscribe landing page.
      newWindow?.close()
      router.push(PORTAL_DBWS_SUBSCRIBE)
    }
  } catch {
    newWindow?.close()
    router.push(PORTAL_DBWS_SUBSCRIBE)
  }
  emits('close')
}

const clickAction = (categoryItem: CategoryItem) => {
  if (haveChildren(categoryItem)) {
    toggleChildren(categoryItem)
    return
  }

  if (isDbwsLoginRoute(categoryItem.slug)) {
    return handleDbwsRedirect()
  }

  const linkAction = getLinkAction(categoryItem)
  if (linkAction) {
    return linkAction()
  }

  // Internal leaf navigation is handled by :to; close the owning menu.
  emits('close')
}

const handleTriggerClick = (event: MouseEvent, categoryItem: CategoryItem) => {
  if (!isActionItem(categoryItem)) {
    return
  }
  event.stopPropagation()
  clickAction(categoryItem)
}
</script>

<style lang="scss" scoped>
@use '~/assets/scss/lib/vars';
@use 'sass:map';

:deep(.list-item-category .v-list-item__overlay) {
  opacity: 0;
}

:deep(.list-item-category.v-list-item--active) {
  color: rgb(var(--v-theme-on-surface));
}

.list-item-category {
  color: rgb(var(--v-theme-on-surface));
  line-height: 21px;
  flex-wrap: wrap;
  align-items: flex-start;
  padding-top: 8px;
  padding-bottom: 8px;
  min-height: auto;
}

.category-item-trigger {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
}

button.category-item-trigger {
  cursor: pointer;
}
.border-bottom {
  border-bottom: 1px solid rgb(var(--v-theme-backgroundButton));
}

.border-top {
  border-top: 1px solid rgb(var(--v-theme-backgroundButton));
}

.desktop-sub-item {
  padding-top: 8px;
  padding-bottom: 8px;
}

.subcategory-list {
  display: flex;
  align-items: stretch;
  padding-top: 8px;
  width: 100%;
}

.subcategory-divider {
  width: 1px;
  background-color: rgba(0, 0, 0, 0.12);
  align-self: stretch;
  margin: 12px 0;
  flex-shrink: 0;
}

.mobile-sub-list {
  padding-left: 0;
}

.mobile-top-item {
  font-size: map.get(vars.$font-sizes, 16);
  line-height: 24px;
}

.mobile-sub-item {
  font-size: map.get(vars.$font-sizes, 14);
}

:deep(
  .mobile-top-item
    > .v-list-item__content
    > .category-item-trigger
    > .v-row:not(.subcategory-list)
),
:deep(
  .mobile-sub-item
    > .v-list-item__content
    > .category-item-trigger
    > .v-row:not(.subcategory-list)
) {
  min-height: 32px;
}
</style>
